// Outcome Tracking Manager

import type {
  Outcome,
  OutcomeUpdate,
  OutcomeStats,
  FrameworkROI,
  OutcomeInsight,
  OutcomeStatus,
  OutcomeCategory,
  ImpactLevel,
  FrameworkApplication,
  Milestone,
} from '../types/outcomes';
import { masterRegistryEntries } from '../data/masterRegistryEntries';

const STORAGE_KEY = 'peak-priorities-outcomes';
const UPDATES_STORAGE_KEY = 'peak-priorities-outcome-updates';

class OutcomeTracker {
  private outcomes: Outcome[] = [];
  private updates: OutcomeUpdate[] = [];

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Load outcomes from localStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const storedUpdates = localStorage.getItem(UPDATES_STORAGE_KEY);
      
      if (stored) {
        this.outcomes = JSON.parse(stored);
      }
      
      if (storedUpdates) {
        this.updates = JSON.parse(storedUpdates);
      }
    } catch (error) {
      console.error('Error loading outcomes from storage:', error);
      this.outcomes = [];
      this.updates = [];
    }
  }

  /**
   * Save outcomes to localStorage
   */
  private saveToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.outcomes));
      localStorage.setItem(UPDATES_STORAGE_KEY, JSON.stringify(this.updates));
    } catch (error) {
      console.error('Error saving outcomes to storage:', error);
    }
  }

  /**
   * Generate a unique ID
   */
  private generateId(): string {
    return `outcome_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Create a new outcome
   */
  createOutcome(data: Omit<Outcome, 'id' | 'createdAt' | 'lastUpdated' | 'progressPercentage' | 'frameworkApplications' | 'milestones'>): Outcome {
    const now = new Date().toISOString();
    
    const outcome: Outcome = {
      ...data,
      id: this.generateId(),
      createdAt: now,
      lastUpdated: now,
      progressPercentage: 0,
      frameworkApplications: [],
      milestones: [],
    };

    this.outcomes.push(outcome);
    this.saveToStorage();
    
    this.addUpdate({
      outcomeId: outcome.id,
      updateType: 'status-change',
      title: 'Outcome Created',
      description: `Started tracking: ${outcome.title}`,
    });

    return outcome;
  }

  /**
   * Get all outcomes
   */
  getOutcomes(): Outcome[] {
    return [...this.outcomes];
  }

  /**
   * Get outcome by ID
   */
  getOutcome(id: string): Outcome | undefined {
    return this.outcomes.find(o => o.id === id);
  }

  /**
   * Update outcome
   */
  updateOutcome(id: string, updates: Partial<Outcome>): Outcome | null {
    const index = this.outcomes.findIndex(o => o.id === id);
    if (index === -1) return null;

    const oldStatus = this.outcomes[index].status;
    
    this.outcomes[index] = {
      ...this.outcomes[index],
      ...updates,
      lastUpdated: new Date().toISOString(),
    };

    this.saveToStorage();

    // Track status changes
    if (updates.status && updates.status !== oldStatus) {
      this.addUpdate({
        outcomeId: id,
        updateType: 'status-change',
        title: 'Status Updated',
        description: `Changed from ${oldStatus} to ${updates.status}`,
      });
    }

    return this.outcomes[index];
  }

  /**
   * Delete outcome
   */
  deleteOutcome(id: string): boolean {
    const index = this.outcomes.findIndex(o => o.id === id);
    if (index === -1) return false;

    this.outcomes.splice(index, 1);
    this.updates = this.updates.filter(u => u.outcomeId !== id);
    this.saveToStorage();
    return true;
  }

  /**
   * Add framework application to outcome
   */
  addFrameworkApplication(outcomeId: string, application: Omit<FrameworkApplication, 'appliedAt'>): boolean {
    const outcome = this.getOutcome(outcomeId);
    if (!outcome) return false;

    const fullApplication: FrameworkApplication = {
      ...application,
      appliedAt: new Date().toISOString(),
    };

    outcome.frameworkApplications.push(fullApplication);
    outcome.lastUpdated = new Date().toISOString();
    
    // Add framework to related frameworks if not already there
    if (!outcome.relatedFrameworks.includes(application.frameworkId)) {
      outcome.relatedFrameworks.push(application.frameworkId);
    }

    this.saveToStorage();

    this.addUpdate({
      outcomeId,
      updateType: 'framework-applied',
      title: 'Framework Applied',
      description: `Applied ${application.frameworkName}`,
      data: fullApplication,
    });

    return true;
  }

  /**
   * Add milestone to outcome
   */
  addMilestone(outcomeId: string, milestone: Omit<Milestone, 'id' | 'status'>): boolean {
    const outcome = this.getOutcome(outcomeId);
    if (!outcome) return false;

    const fullMilestone: Milestone = {
      ...milestone,
      id: this.generateId(),
      status: 'pending',
    };

    outcome.milestones.push(fullMilestone);
    outcome.lastUpdated = new Date().toISOString();
    this.updateProgress(outcomeId);
    this.saveToStorage();

    return true;
  }

  /**
   * Complete milestone
   */
  completeMilestone(outcomeId: string, milestoneId: string): boolean {
    const outcome = this.getOutcome(outcomeId);
    if (!outcome) return false;

    const milestone = outcome.milestones.find(m => m.id === milestoneId);
    if (!milestone) return false;

    milestone.status = 'completed';
    milestone.completedDate = new Date().toISOString();
    outcome.lastUpdated = new Date().toISOString();
    
    this.updateProgress(outcomeId);
    this.saveToStorage();

    this.addUpdate({
      outcomeId,
      updateType: 'milestone-completed',
      title: 'Milestone Completed',
      description: milestone.title,
    });

    return true;
  }

  /**
   * Update progress percentage based on completed milestones
   */
  private updateProgress(outcomeId: string): void {
    const outcome = this.getOutcome(outcomeId);
    if (!outcome || outcome.milestones.length === 0) return;

    const completedCount = outcome.milestones.filter(m => m.status === 'completed').length;
    outcome.progressPercentage = Math.round((completedCount / outcome.milestones.length) * 100);
  }

  /**
   * Add update/activity log
   */
  private addUpdate(data: Omit<OutcomeUpdate, 'id' | 'timestamp'>): void {
    const update: OutcomeUpdate = {
      ...data,
      id: this.generateId(),
      timestamp: new Date().toISOString(),
    };

    this.updates.unshift(update); // Add to beginning
    
    // Keep only last 100 updates
    if (this.updates.length > 100) {
      this.updates = this.updates.slice(0, 100);
    }

    this.saveToStorage();
  }

  /**
   * Get updates for an outcome
   */
  getOutcomeUpdates(outcomeId: string): OutcomeUpdate[] {
    return this.updates.filter(u => u.outcomeId === outcomeId);
  }

  /**
   * Get all recent updates
   */
  getRecentUpdates(limit: number = 10): OutcomeUpdate[] {
    return this.updates.slice(0, limit);
  }

  /**
   * Calculate outcome statistics
   */
  getStats(): OutcomeStats {
    const byStatus: Record<OutcomeStatus, number> = {
      'planning': 0,
      'in-progress': 0,
      'completed': 0,
      'on-hold': 0,
      'cancelled': 0,
    };

    const byCategory: Record<OutcomeCategory, number> = {
      'strategic': 0,
      'operational': 0,
      'team': 0,
      'personal': 0,
      'project': 0,
    };

    const byImpactLevel: Record<ImpactLevel, number> = {
      'low': 0,
      'medium': 0,
      'high': 0,
      'transformational': 0,
    };

    let totalProgress = 0;

    this.outcomes.forEach(outcome => {
      byStatus[outcome.status]++;
      byCategory[outcome.category]++;
      byImpactLevel[outcome.impactLevel]++;
      totalProgress += outcome.progressPercentage;
    });

    const completed = byStatus['completed'];
    const total = this.outcomes.length;
    const active = byStatus['in-progress'];

    return {
      total,
      byStatus,
      byCategory,
      byImpactLevel,
      completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
      averageProgress: total > 0 ? Math.round(totalProgress / total) : 0,
      activeOutcomes: active,
    };
  }

  /**
   * Calculate framework ROI (Return on Investment)
   */
  getFrameworkROI(): FrameworkROI[] {
    const roiMap = new Map<string, {
      timesApplied: number;
      outcomeIds: Set<string>;
      totalEffectiveness: number;
      applications: FrameworkApplication[];
    }>();

    this.outcomes.forEach(outcome => {
      outcome.frameworkApplications.forEach(app => {
        const existing = roiMap.get(app.frameworkId);
        if (existing) {
          existing.timesApplied++;
          existing.outcomeIds.add(outcome.id);
          existing.totalEffectiveness += app.effectiveness;
          existing.applications.push(app);
        } else {
          roiMap.set(app.frameworkId, {
            timesApplied: 1,
            outcomeIds: new Set([outcome.id]),
            totalEffectiveness: app.effectiveness,
            applications: [app],
          });
        }
      });
    });

    const roi: FrameworkROI[] = [];

    roiMap.forEach((data, frameworkId) => {
      const framework = masterRegistryEntries.find(e => e.encodedId === frameworkId);
      if (!framework) return;

      const avgEffectiveness = data.totalEffectiveness / data.timesApplied;
      const impactScore = Math.round(
        (data.timesApplied * 10) + 
        (data.outcomeIds.size * 20) + 
        (avgEffectiveness * 20)
      );

      roi.push({
        frameworkId,
        frameworkName: framework.canonicalName,
        timesApplied: data.timesApplied,
        outcomesImpacted: data.outcomeIds.size,
        averageEffectiveness: Math.round(avgEffectiveness * 10) / 10,
        impactScore,
        recentApplications: data.applications
          .sort((a, b) => new Date(b.appliedAt).getTime() - new Date(a.appliedAt).getTime())
          .slice(0, 3),
      });
    });

    return roi.sort((a, b) => b.impactScore - a.impactScore);
  }

  /**
   * Generate insights
   */
  getInsights(): OutcomeInsight[] {
    const insights: OutcomeInsight[] = [];
    const stats = this.getStats();

    // Completion trend
    if (stats.completionRate >= 70) {
      insights.push({
        type: 'achievement',
        title: 'High Success Rate',
        description: `${stats.completionRate}% of outcomes completed successfully`,
        metric: stats.completionRate,
      });
    } else if (stats.completionRate < 30 && stats.total >= 3) {
      insights.push({
        type: 'warning',
        title: 'Low Completion Rate',
        description: `Only ${stats.completionRate}% completion rate`,
        metric: stats.completionRate,
        actionable: 'Review planning and milestone strategies',
      });
    }

    // Active outcomes
    if (stats.activeOutcomes > 5) {
      insights.push({
        type: 'warning',
        title: 'Many Active Outcomes',
        description: `${stats.activeOutcomes} outcomes currently in progress`,
        metric: stats.activeOutcomes,
        actionable: 'Consider focusing efforts on fewer outcomes',
      });
    }

    // Framework ROI
    const roi = this.getFrameworkROI();
    if (roi.length > 0) {
      const topFramework = roi[0];
      insights.push({
        type: 'trend',
        title: 'Most Valuable Framework',
        description: `${topFramework.frameworkName} has been applied ${topFramework.timesApplied} times across ${topFramework.outcomesImpacted} outcomes`,
        metric: topFramework.impactScore,
        actionable: `Continue leveraging ${topFramework.frameworkName} for new outcomes`,
      });
    }

    // Progress recommendation
    if (stats.averageProgress < 50 && stats.activeOutcomes > 0) {
      insights.push({
        type: 'recommendation',
        title: 'Boost Progress',
        description: `Average progress is ${stats.averageProgress}%`,
        metric: stats.averageProgress,
        actionable: 'Set clearer milestones and track progress regularly',
      });
    }

    return insights;
  }

  /**
   * Clear all data (for testing/reset)
   */
  clearAll(): void {
    this.outcomes = [];
    this.updates = [];
    this.saveToStorage();
  }
}

export const outcomeTracker = new OutcomeTracker();

